/**
 * The session (ADR 0006): the access token lives in memory only, the refresh token in the
 * storage adapter. One refresh attempt per 401, then the session is dropped; concurrent
 * callers share a single in-flight refresh so a burst of 401s cannot rotate the token twice
 * (rotation is one-use on the server, so the second rotation would log the student out).
 */

import { storage } from "./storage.js";
import { ApiClientError, toClientError } from "./errors.js";

export type SubjectType = "student" | "teacher";

export interface Session {
  subjectType: SubjectType;
  accessToken: string;
}

type Listener = (session: Session | null) => void;

let session: Session | null = null;
let refreshing: Promise<string | null> | null = null;
const listeners = new Set<Listener>();

export function getSession(): Session | null {
  return session;
}

/**
 * Which area a token belongs to, read from the access token's own `typ` claim (ADR 0006).
 *
 * The client does not verify the signature — the server does that on every request — it only
 * needs to know which of the two areas the session is for, and it must not guess. Both areas
 * share one browser and one stored refresh token, so a cold boot at `/` with a teacher's token
 * would otherwise be labelled "student" purely because the student screen asked first, and the
 * area guards (build spec 5) would never fire.
 *
 * Returns null for anything unreadable, and the caller keeps its own assumption.
 */
export function subjectTypeFromAccessToken(token: string): SubjectType | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const typ = (JSON.parse(atob(padded)) as { typ?: unknown }).typ;
    return typ === "student" || typ === "teacher" ? typ : null;
  } catch {
    return null;
  }
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit(): void {
  for (const listener of listeners) listener(session);
}

export async function startSession(
  subjectType: SubjectType,
  accessToken: string,
  refreshToken: string,
): Promise<void> {
  session = { subjectType, accessToken };
  await storage.set("refreshToken", refreshToken);
  emit();
}

export async function endSession(): Promise<void> {
  session = null;
  await storage.remove("refreshToken");
  emit();
}

export function setAccessToken(accessToken: string): void {
  if (!session) return;
  session = { ...session, accessToken };
  emit();
}

/**
 * Exchanges the stored refresh token for a new pair (the server rotates it, so the old one is
 * dead afterwards). Returns the new access token, or null when there is no usable session.
 * `subjectType` is only a fallback for an unreadable token: a cold start has no session yet,
 * so the caller names the area it is booting, but the returned access token's `typ` claim
 * wins over it.
 *
 * "The server rejected the token" and "the server could not be reached" must not be confused
 * here. Only the first means the session is over; the second is a Monday morning waking a
 * sleeping Render instance (ADR 0012) or a school Wi-Fi blip, and dropping the session there
 * would make a child re-enter class number, tile and PIN over a hiccup. So a 5xx or a network
 * failure throws `offline` with the token untouched, and the caller shows the offline state.
 */
export async function refreshSession(
  apiUrl: string,
  subjectType: SubjectType,
): Promise<string | null> {
  refreshing ??= (async () => {
    try {
      const refreshToken = await storage.get("refreshToken");
      if (!refreshToken) return null;
      const response = await fetch(`${apiUrl}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
      if (response.status >= 500) {
        throw new ApiClientError("offline", response.status, "refresh endpoint unavailable");
      }
      if (!response.ok) {
        await endSession();
        return null;
      }
      const body = (await response.json()) as { accessToken: string; refreshToken: string };
      // The token says whose it is; `subjectType` is only the caller's assumption and is wrong
      // whenever the stored token belongs to the other area (see `subjectTypeFromAccessToken`).
      await startSession(
        subjectTypeFromAccessToken(body.accessToken) ?? subjectType,
        body.accessToken,
        body.refreshToken,
      );
      return body.accessToken;
    } catch (err) {
      if (err instanceof ApiClientError) throw err;
      throw new ApiClientError("offline", 0, err instanceof Error ? err.message : "network error");
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/**
 * `fetch` with the bearer header and one transparent refresh-and-retry on a 401 (build spec
 * 4.1). Throws `ApiClientError` for anything the caller should show; a failed refresh ends the
 * session, which the router turns into a redirect to the login screen.
 */
export async function authedFetch(
  apiUrl: string,
  path: string,
  init: RequestInit & { subjectType?: SubjectType } = {},
): Promise<Response> {
  const subjectType = init.subjectType ?? session?.subjectType ?? "student";
  const send = (token: string | null) =>
    fetch(`${apiUrl}${path}`, {
      ...init,
      headers: {
        ...(init.body ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });

  let response = await send(session?.accessToken ?? null);
  if (response.status === 401) {
    const token = await refreshSession(apiUrl, subjectType);
    if (!token) {
      await endSession();
      throw new ApiClientError("unauthorized", 401, "session expired");
    }
    response = await send(token);
  }
  if (!response.ok) throw await toClientError(response);
  return response;
}
